import { useState, useEffect, type ReactNode } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { apiUrl } from "../utils/api";
import { useLanguage } from "../context/LanguageContext";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const checkAuthStatus = async () => {
    const token = localStorage.getItem("admin_token");

    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      // Verify token with backend if available
      const response = await fetch(apiUrl("/api/v1/admin/verify"), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setIsAuthenticated(true);
        } else {
          // Invalid token
          localStorage.removeItem("admin_token");
          setIsAuthenticated(false);
        }
      } else if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem("admin_token");
        setIsAuthenticated(false);
      } else {
        // For other HTTP errors, check if token exists and is not obviously invalid
        // This allows offline usage or when backend is down
        setIsAuthenticated(Boolean(token && token.length > 0));
      }
    } catch (error) {
      console.error("Auth verification failed:", error);
      // On network error, check if we have a token and allow access
      // This enables offline usage when backend is unavailable
      setIsAuthenticated(Boolean(token && token.length > 0));
    }

    setIsLoading(false);
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        {t("protected.loading")}
      </div>
    );
  }

  if (!isAuthenticated) {
    const adminPath = location.pathname.split('/')[1] || 'admin';
    return <Navigate to={`/${adminPath}`} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
